import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PlaybackService } from '../../../../core/services/playback.service';
import { CastService, CastDevice } from '../../../../core/services/cast.service';
import { SpotifyDevice, WebPlaybackTrack } from '../../../../shared/models/spotify.models';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-device-selector-dialog',
  templateUrl: './device-selector-dialog.component.html',
  styleUrls: ['./device-selector-dialog.component.scss']
})
export class DeviceSelectorDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  devices: SpotifyDevice[] = [];
  castDevices: CastDevice[] = [];
  currentDevice: SpotifyDevice | null = null;
  currentCastDevice: CastDevice | null = null;
  currentTrack: WebPlaybackTrack | null = null;
  isLoading = true;
  refreshInterval: any;
  isCastAvailable = false;

  constructor(
    private playbackService: PlaybackService,
    public castService: CastService,
    private dialogRef: MatDialogRef<DeviceSelectorDialogComponent>,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadDevices();
    this.startDeviceRefresh();
    this.subscribeToCastState();
    this.subscribeToCurrentTrack();
    this.subscribeToCastSessionReady();
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startDeviceRefresh(): void {
    this.refreshInterval = setInterval(() => {
      this.loadDevices(false);
    }, 5000);
  }

  private subscribeToCastState(): void {
    this.castService.castState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(castState => {
        this.isCastAvailable = castState.isAvailable;
        this.currentCastDevice = castState.currentDevice;
        this.castDevices = this.castService.getCastDevices();
      });
  }

  private subscribeToCurrentTrack(): void {
    this.playbackService.getCurrentTrack()
      .pipe(takeUntil(this.destroy$))
      .subscribe(track => {
        this.currentTrack = track;
      });
  }

  private subscribeToCastSessionReady(): void {
    this.castService.sessionReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleCastSessionReady();
      });
  }

  private async handleCastSessionReady(): Promise<void> {
    if (!this.currentTrack) {
      console.log('No current track to cast');
      this.snackBar.open('No music currently playing to cast', 'Close', { duration: 3000 });
      return;
    }

    if (!this.castService.canCastTrack(this.currentTrack)) {
      const message = this.castService.getCastLimitationMessage(this.currentTrack);
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }

    try {
      console.log('Attempting to load current track on Cast device:', this.currentTrack.name);
      await this.castService.loadSpotifyTrack(this.currentTrack);
      this.snackBar.open(`Now casting: ${this.currentTrack.name} (30s preview)`, 'Close', { duration: 4000 });
    } catch (error) {
      console.error('Failed to load track on Cast device:', error);
      this.snackBar.open('Failed to start casting music', 'Close', { duration: 3000 });
    }
  }

  async loadDevices(showLoading = true): Promise<void> {
    if (showLoading) {
      this.isLoading = true;
    }

    try {
      const response = await this.playbackService.getAvailableDevices();
      this.devices = response.devices || [];
      this.currentDevice = this.devices.find(device => device.is_active) || null;
    } catch (error) {
      console.error('Failed to load devices:', error);
      this.snackBar.open('Failed to load devices', 'Close', { duration: 3000 });
    } finally {
      if (showLoading) {
        this.isLoading = false;
      }
    }
  }

  async selectDevice(device: SpotifyDevice): Promise<void> {
    if (device.is_active) {
      this.dialogRef.close(device);
      return;
    }

    // Return the selected device to the caller so it can decide what to do
    this.dialogRef.close(device);
  }

  async selectCastDevice(): Promise<void> {
    if (!this.isCastAvailable) {
      this.snackBar.open('Google Cast not available', 'Close', { duration: 3000 });
      return;
    }

    try {
      if (this.castService.isConnected()) {
        // Already connected, just show info
        this.snackBar.open(`Already connected to ${this.currentCastDevice?.name}`, 'Close', { duration: 2000 });
        return;
      }

      // Check if current track can be cast
      if (this.currentTrack && !this.castService.canCastTrack(this.currentTrack)) {
        // If track can't be cast, suggest tab mirroring instead
        this.suggestTabMirroring();
        return;
      }

      // Request Cast session for preview playback
      await this.castService.requestSession();
      this.snackBar.open('Connected to Cast device', 'Close', { duration: 2000 });
      
      setTimeout(() => {
        this.closeDialog();
      }, 1000);
    } catch (error) {
      console.error('Failed to connect to Cast device:', error);
      this.snackBar.open('Failed to connect to Cast device', 'Close', { duration: 3000 });
    }
  }

  private suggestTabMirroring(): void {
    if (this.castService.supportsTabMirroring()) {
      const instructions = this.castService.getTabMirroringInstructions();
      this.snackBar.open(instructions, 'Got it', { duration: 8000 });
    } else {
      this.snackBar.open('Full audio casting requires Google Chrome browser', 'Close', { duration: 4000 });
    }
  }

  async castTabAudio(): Promise<void> {
    try {
      this.castService.openChromeTabCasting();
    } catch (error) {
      if (error instanceof Error) {
        this.snackBar.open(error.message, 'Got it', { duration: 10000 });
      }
    }
  }

  async startScreenShare(): Promise<void> {
    try {
      if (!this.castService.isConnected()) {
        this.snackBar.open('Please connect to a Cast device first', 'Close', { duration: 3000 });
        return;
      }
      
      await this.castService.startScreenShareWithAudio();
      this.snackBar.open('Screen sharing started. Make sure to include audio when prompted!', 'Close', { duration: 5000 });
    } catch (error) {
      console.error('Screen share failed:', error);
      if (error instanceof Error) {
        this.snackBar.open(error.message, 'Close', { duration: 6000 });
      }
    }
  }

  async disconnectCast(): Promise<void> {
    if (!this.castService.isConnected()) {
      return;
    }

    try {
      await this.castService.endSession();
      this.snackBar.open('Disconnected from Cast device', 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Failed to disconnect from Cast device:', error);
      this.snackBar.open('Failed to disconnect from Cast device', 'Close', { duration: 3000 });
    }
  }

  getDeviceIcon(deviceType: string): string {
    switch (deviceType.toLowerCase()) {
      case 'computer':
        return 'computer';
      case 'smartphone':
        return 'smartphone';
      case 'speaker':
        return 'speaker';
      case 'tv':
        return 'tv';
      case 'cast_video':
      case 'cast_audio':
        return 'cast';
      default:
        return 'speaker';
    }
  }

  getDeviceTypeLabel(deviceType: string): string {
    switch (deviceType.toLowerCase()) {
      case 'computer':
        return 'Computer';
      case 'smartphone':
        return 'Phone';
      case 'speaker':
        return 'Speaker';
      case 'tv':
        return 'TV';
      case 'cast_video':
        return 'Chromecast';
      case 'cast_audio':
        return 'Cast Audio';
      default:
        return 'Device';
    }
  }

  async refreshDevices(): Promise<void> {
    await this.loadDevices();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  // Template helper methods
  getAvailableDevices(): SpotifyDevice[] {
    return this.devices.filter(device => !device.is_active);
  }

  hasNoOtherDevices(): boolean {
    const hasSpotifyDevices = this.getAvailableDevices().length > 0;
    const hasCastOptions = this.isCastAvailable && !this.currentCastDevice;
    const hasTabMirroring = this.isCastAvailable && this.castService.supportsTabMirroring();
    
    return !hasSpotifyDevices && !hasCastOptions && !hasTabMirroring;
  }

  showCastOption(): boolean {
    return this.isCastAvailable && !this.currentCastDevice;
  }
}